import { RelBox } from '../../../api/ai.api.types';
import { RemoteResult } from '../../common/RemoteResultCommand/index.types';

// Note: there is deliberately NO frame→container coordinate mapping here anymore. All captures
// are cropped to the visible object-fit:cover viewport at the source (frameCapture.
// coverCropRegion), so AI boxes, tracker coordinates and overlay percentages share one space.

// Moves `box` by the same translation the tracker applied to the anchor box (from → to). Used to
// carry the display box along with the locally tracked meter box between server responses.
export const shiftBoxBy = (box: RelBox, from: RelBox, to: RelBox): RelBox => ({
    ...box,
    x: box.x + (to.x - from.x),
    y: box.y + (to.y - from.y),
});

// Maps a box that is relative to a cropped sub-image back into the coordinate space the crop was
// taken from (the visible viewport). `cropRegion` is the viewport-relative region that was sent.
export const boxFromCropSpace = (box: RelBox, cropRegion: RelBox): RelBox => ({
    x: cropRegion.x + box.x * cropRegion.w,
    y: cropRegion.y + box.y * cropRegion.h,
    w: box.w * cropRegion.w,
    h: box.h * cropRegion.h,
});

// Inverse of boxFromCropSpace: converts a viewport-relative box into coordinates relative to a
// cropped sub-image (e.g. to cut the display snippet out of a stored high-res crop).
export const boxToCropSpace = (box: RelBox, cropRegion: RelBox): RelBox => {
    if (cropRegion.w <= 0 || cropRegion.h <= 0) {
        return box;
    }
    return {
        x: (box.x - cropRegion.x) / cropRegion.w,
        y: (box.y - cropRegion.y) / cropRegion.h,
        w: box.w / cropRegion.w,
        h: box.h / cropRegion.h,
    };
};

// A result evaluated from a high-res crop carries crop-relative boxes; this maps every box back
// to viewport coordinates so the result can flow through the normal live-result path (overlay,
// tracker, accumulator) unchanged. Values (meter number, readings) are coordinate-free.
export const mapResultBoxesFromCrop = (result: RemoteResult, cropRegion: RelBox): RemoteResult => {
    const map = (box: RelBox | undefined): RelBox | undefined => (box ? boxFromCropSpace(box, cropRegion) : undefined);
    return {
        ...result,
        detectedMeterBox: map(result.detectedMeterBox),
        detectedDisplayBox: map(result.detectedDisplayBox),
        detectedValueBox: map(result.detectedValueBox),
        meterRates: result.meterRates.map((rate) => ({
            ...rate,
            valueBox: map(rate.valueBox),
            displayBox: map(rate.displayBox),
        })),
    };
};

// Relative geometry of an inner box (display) within an outer box (meter), remembered so the
// meter box can be reconstructed when the AI later only finds the display — in practice the
// high-contrast display is detected far more reliably than the meter housing.
export interface BoxRelation {
    // Inner origin relative to the outer box, in units of the outer size.
    offsetX: number;
    offsetY: number;
    // Inner size as a fraction of the outer size.
    scaleW: number;
    scaleH: number;
}

export const computeBoxRelation = (outer: RelBox, inner: RelBox): BoxRelation | null => {
    if (outer.w <= 0 || outer.h <= 0 || inner.w <= 0 || inner.h <= 0) {
        return null;
    }
    return {
        offsetX: (inner.x - outer.x) / outer.w,
        offsetY: (inner.y - outer.y) / outer.h,
        scaleW: inner.w / outer.w,
        scaleH: inner.h / outer.h,
    };
};

// Inverse of computeBoxRelation: reconstructs the outer (meter) box from a freshly detected
// inner (display) box and the remembered relation.
export const applyBoxRelation = (inner: RelBox, relation: BoxRelation): RelBox | null => {
    if (relation.scaleW <= 0 || relation.scaleH <= 0) {
        return null;
    }
    const w = inner.w / relation.scaleW;
    const h = inner.h / relation.scaleH;
    return {
        x: inner.x - relation.offsetX * w,
        y: inner.y - relation.offsetY * h,
        w,
        h,
    };
};

// Grows a box around its center by `factor`, clamped to the 0..1 frame. The tracker seeds its
// keypoints in this expanded neighborhood: the scene is static and the camera moves, so the wall
// around the meter moves (almost) identically to the meter itself while offering far more texture
// — especially while the meter is still small in the frame. The expansion is deliberately bounded
// instead of using the whole frame by default, to keep the points at a similar scene depth
// (far-away points move differently under camera translation and would distort the scale
// estimate).
export const expandRelBox = (box: RelBox, factor: number): RelBox => {
    const centerX = box.x + box.w / 2;
    const centerY = box.y + box.h / 2;
    const w = box.w * factor;
    const h = box.h * factor;
    const x0 = Math.max(0, centerX - w / 2);
    const y0 = Math.max(0, centerY - h / 2);
    const x1 = Math.min(1, centerX + w / 2);
    const y1 = Math.min(1, centerY + h / 2);
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
};

// Intersection-over-union of two axis-aligned, viewport-relative boxes (0..1). Used to decide
// whether a hi-res crop read's box coincides enough with the current fast box to refine the frame.
export const boxIoU = (a: RelBox, b: RelBox): number => {
    const interW = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
    const interH = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
    if (interW <= 0 || interH <= 0) {
        return 0;
    }
    const interArea = interW * interH;
    const union = a.w * a.h + b.w * b.h - interArea;
    return union > 0 ? interArea / union : 0;
};

// A hi-res crop read's box is adopted as the tracked frame only when it still coincides with the
// last fast-detection box (IoU >= minIoU) — this keeps a remapped crop box from jerking the frame
// away from the accurate fast box. With no fast box yet (bootstrap) any candidate is accepted.
export const shouldAdoptHiResBox = (candidate: RelBox | undefined, lastFastBox: RelBox | null, minIoU: number): boolean => {
    if (!candidate) {
        return false;
    }
    if (!lastFastBox) {
        return true;
    }
    return boxIoU(candidate, lastFastBox) >= minIoU;
};
